/**
 * Gate de aceite (§21.10).
 *
 * A tela que a pessoa vê quando há documento contratual pendente. Ela não é o
 * bloqueio — o bloqueio está no guarda de sessão do servidor, que recusa toda
 * rota do produto com `LEGAL_ACCEPTANCE_REQUIRED`. Esta tela é a PORTA: sem ela,
 * a pessoa entraria e receberia erro atrás de erro sem saber o que fazer.
 *
 * Três decisões que valem explicação:
 *
 * 1. **Nada vem pré-marcado.** Um aceite pré-marcado não é manifestação de
 *    vontade; é a ausência de recusa. A mesma disciplina que vale para cookies
 *    opcionais vale aqui, com mais razão: isto é condição do contrato.
 *
 * 2. **O texto fica na própria tela.** Aceitar um link é aceitar algo que não se
 *    viu. O documento é apresentado inteiro, com versão e hash visíveis.
 *
 * 3. **Existe saída.** Quem não quer aceitar sai da sessão. Prender alguém numa
 *    conta que não faz nada seria o oposto de consentimento livre — e a saída
 *    precisa ter o mesmo peso visual de qualquer outro caminho.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { BRANDING } from '../config/branding';
import { SimboloCeres } from '../components/Marca';
import { ErrorState, LoadingRows, Spinner } from '../components/ui';
import { TextoLegal } from '../components/TextoLegal';
import { t } from '../lib/i18n';

interface Pendente {
  chave: string;
  titulo: string;
  versao: string;
}

interface Documento {
  chave: string;
  titulo: string;
  versao: string;
  hash: string;
  conteudo: string;
}

type Idioma = 'pt-BR' | 'en';

export function AceiteDeTermosPage(): JSX.Element {
  const { refresh, logout } = useSession();
  const navigate = useNavigate();

  const [idioma, setIdioma] = useState<Idioma>('pt-BR');
  const [documentos, setDocumentos] = useState<Documento[] | null>(null);
  const [aceitos, setAceitos] = useState<Record<string, boolean>>({});
  const [aberto, setAberto] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async (alvo: Idioma): Promise<void> => {
    setDocumentos(null);
    setErro(null);
    try {
      const situacao = await api.get<{ pendentes: Pendente[] }>(
        `/legal/situacao?idioma=${alvo}`,
      );
      const textos = await Promise.all(
        situacao.pendentes.map((p) =>
          api.get<Documento>(`/legal/documentos/${p.chave}?idioma=${alvo}`),
        ),
      );
      setDocumentos(textos);
      // A marcação é ZERADA a cada carregamento, inclusive na troca de idioma:
      // o hash do texto muda com o idioma, e uma caixa marcada sobre o texto em
      // português não vale como aceite do texto em inglês.
      setAceitos({});
      setAberto(textos[0]?.chave ?? null);
    } catch (e) {
      setErro(e);
    }
  }, []);

  useEffect(() => {
    void carregar(idioma);
  }, [carregar, idioma]);

  const todosMarcados =
    documentos !== null && documentos.length > 0 && documentos.every((d) => aceitos[d.chave] === true);

  const enviar = async (): Promise<void> => {
    if (documentos === null || !todosMarcados) return;
    setEnviando(true);
    setErro(null);
    try {
      await api.post('/legal/aceites', {
        documentos: documentos.map((d) => ({ chave: d.chave, versao: d.versao, hash: d.hash })),
        idioma,
      });
      // A sessão é reconsultada ANTES de navegar: é ela que destranca o gate, e
      // navegar primeiro devolveria a pessoa para cá pelo estado antigo.
      await refresh();
      navigate('/', { replace: true });
    } catch (e) {
      // 409 é "o texto mudou desde que a tela abriu". Recarregar é a única saída
      // honesta: aceitar o texto de antes não vale para o texto de agora.
      if (e instanceof ApiError && e.status === 409) {
        setErro(e);
        void carregar(idioma);
      } else {
        setErro(e);
      }
    } finally {
      setEnviando(false);
    }
  };

  const sair = async (): Promise<void> => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-sunken">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <SimboloCeres className="h-6 w-6 text-brand-600" decorativa />
            <span className="text-sm font-semibold text-ink">{BRANDING.productName}</span>
          </div>
          <div className="flex gap-1" role="group" aria-label={t('aceite.idiomaDocumentos')}>
            {(
              [
                ['pt-BR', 'Português'],
                ['en', 'English'],
              ] as const
            ).map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setIdioma(valor)}
                aria-pressed={idioma === valor}
                className={`rounded px-2 py-1 text-2xs font-medium ${
                  idioma === valor ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6">
        <h1 className="text-xl font-semibold text-ink">{t('aceite.antesDeContinuar')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('aceite.condicaoDeUso')}</p>

        {erro !== null && (
          <div className="mt-4">
            <ErrorState error={erro} />
          </div>
        )}

        {documentos === null ? (
          <div className="mt-6">
            <LoadingRows rows={6} columns={1} />
          </div>
        ) : documentos.length === 0 ? (
          <p className="mt-6 text-sm text-ink-muted">{t('aceite.semPendentes')}</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {documentos.map((doc) => (
              <li key={doc.chave} className="rounded-lg border border-line bg-surface">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                  <div>
                    <h2 className="text-sm font-semibold text-ink">{doc.titulo}</h2>
                    {/* Versão e hash à vista: é o que permite a alguém dizer
                        depois "aceitei a versão 1.0, hash a3f…" e ser levado a
                        sério. Num rodapé, seriam o mesmo que ausentes. */}
                    <p className="mt-0.5 font-mono text-2xs text-ink-subtle">
                      versão {doc.versao} · hash {doc.hash.slice(0, 16)}…
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    aria-expanded={aberto === doc.chave}
                    onClick={() => setAberto(aberto === doc.chave ? null : doc.chave)}
                  >
                    {aberto === doc.chave ? 'Recolher' : 'Ler o documento'}
                  </button>
                </div>

                {aberto === doc.chave && (
                  <div className="max-h-96 overflow-y-auto px-4 py-3">
                    <TextoLegal markdown={doc.conteudo} />
                  </div>
                )}

                <div className="border-t border-line px-4 py-3">
                  <label className="flex items-start gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={aceitos[doc.chave] === true}
                      onChange={(e) =>
                        setAceitos((atual) => ({ ...atual, [doc.chave]: e.target.checked }))
                      }
                    />
                    <span>
                      {t('aceite.liEAceito')}{' '}
                      <strong className="font-semibold">{doc.titulo}</strong>, {t('aceite.versao')}{' '}
                      {doc.versao}.
                    </span>
                  </label>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-primary"
            disabled={!todosMarcados || enviando}
            onClick={() => void enviar()}
          >
            {enviando && <Spinner className="mr-2 h-4 w-4" />}
            Aceitar e continuar
          </button>
          {/* Mesmo peso visual e mesmo número de cliques que o aceite: a saída
              não pode ser um link escondido, ou a escolha deixa de ser livre. */}
          <button type="button" className="btn-secondary" onClick={() => void sair()}>{t('aceite.sairSemAceitar')}</button>
        </div>
        {!todosMarcados && documentos !== null && documentos.length > 0 && (
          <p className="mt-2 text-xs text-ink-muted">{t('aceite.marqueCada')}</p>
        )}
      </main>
    </div>
  );
}
